/**
 * «Где эти деньги в банке» — for any document that can have a bank trace.
 *
 * The owner asked for the system to work as one thing. The bank layer already
 * knew that a given invoice was paid by a specific row on a specific day; the
 * document's own card had no way to ask, so the knowledge stayed inside the
 * cash-flow page.
 *
 * Three answers, and the third is not a failure:
 *   linked          — these bank rows are this money;
 *   in_settlement   — it arrived inside a gateway batch, which is a transit of
 *                     many invoices at once and never links one-to-one;
 *   none            — no pairing found, which for card payments is simply
 *                     normal (61 % of the expense side has no document at all).
 *
 * Rendered only for roles that may see bank data: descriptions carry the names,
 * addresses and account numbers of clients and contractors.
 */
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useDocumentCashFlowQuery } from 'core';
import { useUser } from 'core/hooks';
import {
  FINANCE_PLAN_ROLES,
  visibleFor,
} from 'core/hooks/Palette/paletteRoles';
import { UserRole } from 'types';

import * as S from './CashFlowEvidence.styled';

export type CashFlowTarget = 'invoice' | 'expense' | 'payment';

interface IProps {
  targetType: CashFlowTarget;
  targetId: number | string;
}

const MONEY = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 2,
});

export const CashFlowEvidence = memo(({ targetType, targetId }: IProps) => {
  const { t } = useTranslation();
  const [{ user }] = useUser();
  const allowed = visibleFor(
    (user?.roles ?? []) as UserRole[],
    FINANCE_PLAN_ROLES,
  );

  const { data, loading } = useDocumentCashFlowQuery({
    variables: { targetType, targetId: String(targetId) },
    fetchPolicy: 'cache-first',
    skip: !allowed || !targetId,
  });

  if (!allowed) return null;

  const payload = data?.documentCashFlow;
  if (loading && !payload) return null;
  if (!payload) return null;

  const rows = (payload.rows || []).filter((row): row is NonNullable<
    typeof row
  > => Boolean(row));

  return (
    <S.Block>
      <S.Title>{t('molecules.cashFlowEvidence.title')}</S.Title>

      {payload.status === 'linked' && (
        <S.Rows>
          {rows.map((row) => (
            <S.Row key={row!.pk!}>
              <S.When>
                {row!.bookedAt
                  ? moment(row!.bookedAt).format('DD.MM.YYYY')
                  : '—'}
              </S.When>
              <S.Amount negative={(row!.amount || 0) < 0}>
                {MONEY.format(row!.amount || 0)}
              </S.Amount>
              <S.Where title={row!.description || ''}>
                {row!.accountName}
                {row!.counterpartyName ? ` · ${row!.counterpartyName}` : ''}
              </S.Where>
            </S.Row>
          ))}
        </S.Rows>
      )}

      {payload.status === 'in_settlement' && (
        <S.Note>
          {t('molecules.cashFlowEvidence.inSettlement', {
            provider: payload.settlementProvider || '—',
            batch: payload.settlementRef || '—',
            date: payload.settlementBookedAt
              ? moment(payload.settlementBookedAt).format('DD.MM.YYYY')
              : '—',
          })}
        </S.Note>
      )}

      {payload.status === 'none' && (
        <S.Muted>{t('molecules.cashFlowEvidence.none')}</S.Muted>
      )}
    </S.Block>
  );
});

CashFlowEvidence.displayName = 'CashFlowEvidence';
